import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../../components/Pagination";

const SubGroup = () => {
  const [data, setData] = useState([]);
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  // Fetch group data
  const fetchGroupData = async () => {
    setLoading(true);
    try {
      const GroupSearch = getUrlByOperation("GroupSearch") || 
        "https://api.dqr-display-tms.bonrix.in/api/GroupOperation/SubUser/Search";
      
      const response = await axios.post(
        GroupSearch,
        {
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        setHasMore(response.data.data.length === dataLimit);
      } else {
        setData([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching group data:", error);
      setMessage("Failed to fetch group data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupData();
  }, [getUrlByOperation, token, userid, dataLimit, count]);

  const handleAddGroup = () => {
    navigate("/addgroup");
  };

  const handleUpdateGroup = (id) => {
    navigate(`/updategroup/${id}`);
  };

  const handleViewSubGroup = (id) => {
    navigate(`/subgroup/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      try {
        const GroupDelete = getUrlByOperation("GroupDelete") || 
          "https://api.dqr-display-tms.bonrix.in/api/GroupOperation/SubUser/Delete";
        
        await axios.post(
          GroupDelete,
          { id },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        );
        setMessage("Group deleted successfully!");
        fetchGroupData(); // Refresh data after delete
      } catch (error) {
        console.error("Error deleting group:", error);
        setMessage("Failed to delete group");
      }
    }
  };

  const handleToggle = async (item) => {
    try {
      const apiEndpoint = item.active 
        ? "https://api.dqr-display-tms.bonrix.in/api/GroupOperation/SubUser/UpdateStatus/Deactive"
        : "https://api.dqr-display-tms.bonrix.in/api/GroupOperation/SubUser/UpdateStatus/Active";
      
      const GroupActive = getUrlByOperation("GroupActive");
      const GroupDeactive = getUrlByOperation("GroupDeactive");
      
      const url = item.active 
        ? (GroupDeactive || apiEndpoint.replace("Active", "Deactive"))
        : (GroupActive || apiEndpoint);

      await axios.post(
        url,
        { id: item.id },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
      setMessage(item.active ? "Group deactivated successfully!" : "Group activated successfully!");
    } catch (error) {
      console.error("Error updating group status:", error);
      setMessage("Failed to update group status");
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Clean up message after display
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Pagination navigation functions
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0); // Reset to first page when changing items per page
  };

  const goFirst = () => {
    if (count !== 0) setCount(0);
  };
  
  const goPrev = () => {
    if (count > 0) setCount(count - 1);
  };
  
  const goNext = () => {
    if (hasMore) setCount(count + 1);
  };

  const handlePageChange = (page) => {
    setCount(page - 1); // Convert 1-based page to 0-based index
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">Groups</h1>
                <button 
                  type="button" 
                  className="btn btn-success" 
                  onClick={handleAddGroup}
                >
                  Add Group
                </button>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <hr />
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover table-striped">
                        <thead className="thead-light">
                          <tr>
                            <th style={{ width: "5%" }} className="text-center">
                              ID
                            </th>
                            <th style={{ width: "20%" }}>Group Name</th>
                            <th style={{ width: "30%" }}>Description</th>
                            <th style={{ width: "20%" }} className="text-center">
                              Actions
                            </th>
                            <th style={{ width: "15%" }} className="text-center">
                              Active
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="5" className="text-center">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                {" "}Loading...
                              </td>
                            </tr>
                          ) : data.length > 0 ? (
                            data.map((item, index) => (
                              <tr key={index}>
                                <td className="text-center">{item.id}</td>
                                <td>
                                  <strong>{item.groupname || item.name || "-"}</strong>
                                </td>
                                <td>{item.description || item.remark || "-"}</td>
                                <td className="text-center">
                                  <div className="btn-group">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-info mr-1"
                                      onClick={() => handleViewSubGroup(item.id)}
                                      title="View SubGroup"
                                    >
                                      <i className="fas fa-list"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary mr-1"
                                      onClick={() => handleUpdateGroup(item.id)}
                                      title="Edit Group"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleDelete(item.id)}
                                      title="Delete Group"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <Switch
                                    isOn={item.active}
                                    handleToggle={() => handleToggle(item)}
                                    id={item.id}
                                  />
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">
                                No groups found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <Pagination
                      currentPage={count + 1}
                      totalPages={hasMore ? count + 2 : count + 1}
                      onPageChange={handlePageChange}
                      itemsPerPage={dataLimit}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      totalItems={data.length + (hasMore ? dataLimit : 0)}
                      showPageNumbers={false}
                      showItemsPerPageSelector={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Message display */}
      {message && (
        <div style={{ position: "fixed", top: "20px", right: "10px", width: "25%", zIndex: "9999" }}>
          <div
            className={`alert ${
              message.includes("successfully") ? "alert-success" : "alert-danger"
            } d-flex align-items-center`}
            role="alert"
            style={{ marginBottom: "10px" }}
          >
            <div>{message}</div>
          </div>
        </div>
      )}

      {message.includes("successfully") && <SuccessMessage message={message} />}
      {message.includes("Failed") && <FailedMessage message={message} />}
    </>
  );
};

export default SubGroup;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}